import { apiDelete, apiGet, apiPost, apiPut, apiPostBlob } from "../_apiShim";
import { DocumentTemplate } from "@/types/DocumentTemplate";
import {
  builderIfMatchFrom,
  builderReadTicket,
  builderVersionFor,
  rememberBuilderVersion,
  settleBuilderVersion,
} from "./_builderVersionStore";

const BASE_PATH = "/api/v1/template/document-template/";

export async function getTemplateDocumentTemplates(
  templateId: string,
): Promise<DocumentTemplate[]> {
  const response = await apiGet<DocumentTemplate[]>(
    `${BASE_PATH}${templateId}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
  return response ?? [];
}

export async function getTemplateDocumentTemplate(
  templateId: string,
  documentTemplateId: string,
): Promise<DocumentTemplate> {
  const ticket = builderReadTicket(documentTemplateId);
  const template = await apiGet<DocumentTemplate>(
    `${BASE_PATH}${templateId}/${documentTemplateId}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
  // W2-F (M19): remember the loaded version for the If-Match echo on save.
  rememberBuilderVersion(documentTemplateId, (template as { version?: number })?.version, ticket);
  return template;
}

export async function createTemplateDocumentTemplate(
  templateId: string,
  template: Partial<DocumentTemplate>,
): Promise<string> {
  const response = await apiPost<string>(
    `${BASE_PATH}${templateId}`,
    template,
    {
      "Cache-Control": "no-store",
    },
  );
  if (!response) throw new Error("unknown");
  return response;
}

export async function updateTemplateDocumentTemplate(
  templateId: string,
  documentTemplateId: string,
  template: Partial<DocumentTemplate>,
): Promise<void> {
  const sent = builderVersionFor(documentTemplateId);
  await apiPut<void>(
    `${BASE_PATH}${templateId}/${documentTemplateId}`,
    template,
    {
      "Cache-Control": "no-store",
      // W2-F (M19): echo the loaded version — backend 409s stale writes.
      ...builderIfMatchFrom(sent),
    },
  );
  settleBuilderVersion(documentTemplateId, sent);
}

export async function deleteTemplateDocumentTemplate(
  templateId: string,
  documentTemplateId: string,
): Promise<void> {
  await apiDelete<void>(
    `${BASE_PATH}${templateId}/${documentTemplateId}`,
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function duplicateTemplateDocumentTemplate(
  templateId: string,
  documentTemplateId: string,
): Promise<string> {
  const response = await apiPost<string>(
    `${BASE_PATH}${templateId}/${documentTemplateId}/duplicate`,
    {},
    {
      "Cache-Control": "no-store",
    },
  );
  if (!response) throw new Error("unknown");
  return response;
}

export async function downloadTemplateDocumentPdf(
  templateId: string,
  documentTemplateId: string,
  htmlPages: string[],
  widthPx: number,
  heightPx: number,
  fileName: string,
): Promise<Uint8Array> {
  return await apiPostBlob(
    `${BASE_PATH}${templateId}/${documentTemplateId}/download-pdf`,
    { htmlPages, widthPx, heightPx, fileName },
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function presignTemplateDocumentFile(
  templateId: string,
  documentTemplateId: string,
  data: { fileName: string; contentType: string; fileSize: number },
): Promise<{ uploadUrl: string; s3Key: string; fileId: string }> {
  return await apiPost<{ uploadUrl: string; s3Key: string; fileId: string }>(
    `${BASE_PATH}${templateId}/${documentTemplateId}/files/presign`,
    data,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function confirmTemplateDocumentFile(
  templateId: string,
  documentTemplateId: string,
  data: {
    s3Key: string;
    fileName: string;
    fileSize: number;
    contentType: string;
    targetField: string;
  },
): Promise<void> {
  await apiPost<void>(
    `${BASE_PATH}${templateId}/${documentTemplateId}/files/confirm`,
    data,
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function deleteTemplateDocumentFile(
  templateId: string,
  documentTemplateId: string,
  fileId: string,
): Promise<void> {
  await apiDelete<void>(
    `${BASE_PATH}${templateId}/${documentTemplateId}/files/${fileId}`,
    {
      "Cache-Control": "no-store",
    },
  );
}
